<template>
  <div
    class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 animate-modalFade"
    v-if="!isClosed"
  >
    <div
      class="w-full max-w-2xl bg-gray-800 text-gray-300 rounded-lg p-5 flex flex-col gap-4 relative shadow-lg border border-gray-700 animate-scaleIn"
    >
      <!-- Header -->
      <header class="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-700 pb-3 gap-3 sm:gap-0">
        <div class="flex items-center gap-2 min-w-0 flex-1 w-full">
          <i class="fas fa-store text-indigo-300 text-lg"></i>
          <input
            v-if="isEditingName"
            v-model="editedShopName"
            type="text"
            class="flex-1 bg-gray-900 text-indigo-200 text-xl font-semibold border border-gray-700 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="ชื่อร้านค้าใหม่"
          />
          <h2 v-else class="text-xl font-semibold text-indigo-200 truncate flex-1">
            {{ shopName || 'ไม่มีชื่อ' }}
          </h2>
          <button
            v-if="isEditingName"
            @click="saveShopName"
            class="ml-2 text-green-400 hover:text-green-300"
            title="บันทึกชื่อร้านค้า"
            :disabled="isSaving"
          >
            <i class="fas fa-save text-lg"></i>
          </button>
          <button
            v-else
            @click="startEditingName"
            class="ml-2 text-blue-400 hover:text-blue-300"
            title="แก้ไขชื่อร้านค้า"
          >
            <i class="fas fa-edit text-lg"></i>
          </button>
        </div>
        <div class="flex items-center gap-2 w-full sm:w-auto justify-start sm:justify-end">
          <!-- Hide Identity Toggle -->
          <div
            class="flex items-center gap-2 py-1 px-2 rounded-md bg-gray-900"
            role="switch"
            :aria-checked="hideIdentity"
            @click="toggleHideIdentity"
          >
            <div
              class="w-10 h-5 rounded-full border border-gray-700 relative transition-colors duration-200"
              :class="hideIdentity ? 'bg-indigo-600 bg-opacity-40 border-indigo-600' : 'bg-gray-700 bg-opacity-70'"
            >
              <div
                class="w-4 h-4 rounded-full bg-white absolute top-1/2 transform -translate-y-1/2 transition-all duration-200"
                :class="hideIdentity ? 'left-6' : 'left-1'"
              ></div>
            </div>
            <span class="text-xs text-gray-400 hidden sm:inline">{{ hideIdentity ? 'ซ่อนตัวตน' : 'แสดงตัวตน' }}</span>
          </div>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-indigo-300 transition-colors duration-200"
            title="ปิดหน้าต่าง"
          >
            <i class="fas fa-times text-lg"></i>
          </button>
        </div>
      </header>

      <!-- Status / Countdown (Simple) -->
      <section
        v-if="timeLeft !== null"
        class="flex items-center justify-center gap-2 py-2 px-3 border border-gray-700 rounded-md bg-gray-900 bg-opacity-40"
      >
        <i class="far fa-clock text-indigo-300 text-sm"></i>
        <span class="text-sm text-indigo-200 font-semibold">จะปิดร้านค้าภายใน:</span>
        <span class="font-mono text-yellow-200 tracking-widest text-sm">
          {{ formatTime(timeLeft) }}
        </span>
      </section>

      <!-- Loading -->
      <section v-if="isLoading" class="flex flex-col items-center justify-center py-6">
        <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-red-600"></div>
        <p class="text-gray-300 text-sm mt-2">กำลังโหลด...</p>
      </section>

      <!-- Items -->
      <section v-else class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold text-indigo-200">สินค้าที่กำลังขาย</h3>
          <div class="flex items-center gap-2">
            <span class="chip text-2xs">ทั้งหมด {{ items.length }} ชิ้น</span>
            <button
              @click="openAddItemModal"
              class="bg-indigo-600 hover:bg-indigo-700 text-white text-xs py-1 px-2 rounded-md transition-colors duration-200 border border-indigo-700"
              title="เพิ่มสินค้า"
            >
              <i class="fas fa-plus mr-1"></i> เพิ่มสินค้า
            </button>
          </div>
        </div>

        <div
          class="bg-gray-800 rounded-md p-3 max-h-96 overflow-y-auto scrollbar-thin border border-gray-700"
        >
          <div v-if="items.length" class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <article
              v-for="(item, index) in items"
              :key="item.id"
              class="flex items-center gap-3 p-2 rounded-md border border-gray-700 bg-gray-900 bg-opacity-30 transition-all duration-200 hover:border-indigo-600"
            >
              <img
                :src="item.img || defaultItemImg"
                alt="Item"
                class="w-14 h-14 rounded-md object-cover border-2 flex-shrink-0"
                :class="item.rarityColors?.borderColor || 'border-gray-600'"
                @error="handleImageError"
              />
              <div class="flex-1 min-w-0">
                <p
                  class="text-xs font-semibold truncate"
                  :class="item.rarityColors?.textColor || 'text-gray-300'"
                  :title="item.name"
                >
                  {{ item.name }} {{ item.level ? `+${item.level}` : '' }}
                  <i
                    v-if="item.transfer"
                    class="fas fa-arrow-right-arrow-left text-green-200 text-2xs ml-1"
                  ></i>
                </p>
                <!-- Stats badges from gameItems.json -->
                <div v-if="item.stats && Object.values(item.stats).some(v => v > 0)" class="flex flex-wrap gap-1 mt-0.5">
                  <span v-if="item.stats.stamina > 0" class="text-2xs bg-gray-800 text-indigo-300 px-1 rounded border border-gray-700">STA {{ item.stats.stamina }}</span>
                  <span v-if="item.stats.str > 0" class="text-2xs bg-gray-800 text-indigo-300 px-1 rounded border border-gray-700">STR {{ item.stats.str }}</span>
                  <span v-if="item.stats.agi > 0" class="text-2xs bg-gray-800 text-indigo-300 px-1 rounded border border-gray-700">AGI {{ item.stats.agi }}</span>
                  <span v-if="item.stats.int > 0" class="text-2xs bg-gray-800 text-indigo-300 px-1 rounded border border-gray-700">INT {{ item.stats.int }}</span>
                  <span v-if="item.stats.luck > 0" class="text-2xs bg-gray-800 text-indigo-300 px-1 rounded border border-gray-700">LUK {{ item.stats.luck }}</span>
                  <span v-if="item.stats.maxHp > 0" class="text-2xs bg-gray-800 text-indigo-300 px-1 rounded border border-gray-700">HP {{ item.stats.maxHp }}</span>
                  <span v-if="item.stats.atk > 0" class="text-2xs bg-gray-800 text-indigo-300 px-1 rounded border border-gray-700">ATK {{ item.stats.atk }}</span>
                  <span v-if="item.stats.defense > 0" class="text-2xs bg-gray-800 text-indigo-300 px-1 rounded border border-gray-700">DEF {{ item.stats.defense }}</span>
                  <span v-if="item.stats.critical > 0" class="text-2xs bg-gray-800 text-indigo-300 px-1 rounded border border-gray-700">CRT {{ item.stats.critical }}</span>
                  <span v-if="item.stats.evasion > 0" class="text-2xs bg-gray-800 text-indigo-300 px-1 rounded border border-gray-700">EVA {{ item.stats.evasion }}</span>
                </div>
                <div class="flex items-center gap-2 mt-0.5">
                  <p class="text-2xs text-gray-400">ราคา:</p>
                  <input
                    v-if="editedPrices[item.id] !== undefined"
                    v-model.number="editedPrices[item.id]"
                    type="number"
                    min="10"
                    max="100000"
                    step="1"
                    class="w-24 bg-gray-900 text-gray-200 border border-gray-700 rounded-md px-1 py-0.5 text-2xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <span v-else class="text-2xs text-gray-400">{{ item.price }} เพชร</span>
                  <button
                    v-if="editedPrices[item.id] !== undefined"
                    @click.stop="saveItemPrice(item.id, index)"
                    class="text-green-400 hover:text-green-300 text-xs"
                    title="บันทึกราคา"
                    :disabled="isSaving"
                  >
                    <i class="fas fa-save"></i>
                  </button>
                  <button
                    v-else
                    @click.stop="startEditingPrice(item.id, item.price)"
                    class="text-blue-400 hover:text-blue-300 text-xs"
                    title="แก้ไขราคา"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                </div>
              </div>
              <!-- Info Button -->
              <button
                @click.stop="openItemDetail(item)"
                class="text-gray-400 hover:text-indigo-300 text-xs"
                title="ดูรายละเอียดไอเทม"
              >
                <i class="fas fa-info-circle"></i>
              </button>
              <!-- Remove Item Button -->
              <button
                @click.stop="removeItemFromShop(item.id, index)"
                class="text-red-400 hover:text-red-300 text-xs"
                title="ยกเลิกขายไอเทมนี้"
                :disabled="isSaving"
              >
                <i class="fas fa-trash"></i>
              </button>
            </article>
          </div>

          <div v-else class="text-center text-gray-400 text-xs py-6">
            ไม่มีสินค้าที่กำลังขาย
          </div>
        </div>
      </section>

      <!-- Actions -->
      <footer class="flex flex-col gap-2">
        <button
          @click="handleCancelShopClick"
          class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 border"
          :class="
            isConfirmingCancel
              ? 'bg-yellow-600 hover:bg-yellow-700 border-yellow-700'
              : 'bg-indigo-600 hover:bg-indigo-700 border-indigo-700'
          "
          :disabled="isLoading || isCanceling"
        >
          <div v-if="isCanceling" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-times text-xs"></i>
          {{ isConfirmingCancel ? 'ยืนยันปิดร้านค้า' : 'ปิดร้านค้า' }}
        </button>

        <p
          v-if="message"
          class="text-xs text-center"
          :class="messageIsError ? 'text-red-300' : 'text-yellow-200'"
        >
          {{ message }}
        </p>
      </footer>
    </div>
  </div>

  <!-- Add Item Modal -->
  <transition name="fade">
    <div v-if="showAddItemModal" class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 animate-modalFade">
      <div class="w-full max-w-2xl bg-gray-800 text-gray-300 rounded-lg p-0 flex flex-col relative shadow-lg border border-gray-700 animate-scaleIn">
        <!-- Head -->
        <div class="flex justify-between items-center border-b border-gray-700 px-4 py-3 sticky top-0 bg-gray-900 bg-opacity-95 backdrop-filter backdrop-blur">
          <h3 class="text-lg font-semibold text-indigo-200"><i class="fas fa-suitcase mr-2"></i> เพิ่มไอเทมเข้าไปในร้าน</h3>
          <button class="text-gray-400 hover:text-indigo-300 transition-colors duration-200" @click="showAddItemModal = false" :disabled="isActionLoading" aria-label="ปิด">
            <i class="fas fa-times text-lg"></i>
          </button>
        </div>
        <!-- Content -->
        <div class="p-4 overflow-auto max-h-96 scrollbar-custom">
          <div class="rounded-md border border-yellow-700 bg-yellow-900 bg-opacity-10 text-yellow-200 p-3 mb-3 text-sm">
            <i class="fas fa-exclamation-triangle mr-1"></i> ไอเทมประเภทอุปกรณ์ (เช่น หมวก, เสื้อ, อาวุธ) ต้องมีระดับ 8 ดาวขึ้นไปจึงจะสามารถตั้งขายได้
          </div>
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <div
              v-for="it in itemsForAdd"
              :key="it.id"
              class="border border-gray-700 rounded-md bg-gray-900 p-2 cursor-pointer transition-all duration-200 hover:border-indigo-600"
              :class="it.rarityColors?.borderColor || 'border-gray-700'"
              @click="selectAddItem(it)"
            >
              <div class="w-full aspect-square bg-gray-800 border border-gray-700 rounded-md overflow-hidden flex items-center justify-center">
                <img
                  :src="it.img || defaultItemImg"
                  :class="it.rarityColors?.borderColor || 'border-gray-700'"
                  alt=""
                  class="w-full h-full object-cover border rounded"
                  @error="handleImageError"
                />
              </div>
              <div class="mt-2">
                <div class="text-sm font-semibold truncate" :class="it.rarityColors?.textColor || 'text-gray-200'">
                  {{ it.name }} {{ it.level ? `+${it.level}` : '' }}
                </div>
                <div class="text-2xs text-gray-400"><i class="fas fa-tag mr-1"></i>{{ it.type }}</div>
                <div v-if="it.rarity" class="text-2xs flex items-center gap-1">
                  <i class="fas fa-star" :style="{ color: it.rarity === 7 ? '#FFD700' : '#c7caff' }"></i>
                  {{ it.rarity }} ดาว
                </div>
              </div>
              <div class="mt-2 text-sm text-indigo-200 flex items-center gap-1">
                <i class="fas fa-plus-circle"></i> เพิ่ม
              </div>
            </div>
            <div v-if="!itemsForAdd.length" class="col-span-full text-xs text-gray-400 border border-dashed border-gray-700 rounded-md px-2 py-2 text-center">
              ไม่มีไอเทมที่สามารถเพิ่มได้
            </div>
          </div>
        </div>
        <!-- Actions -->
        <div class="flex gap-2 border-t border-gray-700 px-4 py-3 sticky bottom-0 bg-gray-900 bg-opacity-95 backdrop-filter backdrop-blur">
          <button
            class="bg-gray-706 hover:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-gray-600"
            @click="showAddItemModal = false"
            :disabled="isActionLoading"
          >
            <i class="fas fa-times mr-2 text-xs"></i> ยกเลิก
          </button>
        </div>
      </div>
    </div>
  </transition>

  <!-- Detail Item Modal -->
  <transition name="fade">
    <DetailItemModal
      v-if="showDetailModal && selectedItem"
      :item="selectedItem"
      :item-id="selectedItem.id"
      @close="closeDetailModal"
    />
  </transition>

  <!-- Add Item Price Modal (when selecting an item to add) -->
  <transition name="fade">
    <div v-if="showAddPriceModal" class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 animate-modalFade">
      <div class="w-full max-w-md bg-gray-800 text-gray-300 rounded-lg p-4 flex flex-col gap-4 relative shadow-lg border border-gray-700 animate-scaleIn">
        <!-- Head -->
        <div class="flex justify-between items-center">
          <h3 class="text-lg font-semibold text-indigo-200">ตั้งราคาไอเทม</h3>
          <button class="text-gray-400 hover:text-indigo-300" @click="showAddPriceModal = false">
            <i class="fas fa-times text-lg"></i>
          </button>
        </div>
        <!-- Content -->
        <div class="flex flex-col gap-2">
          <p class="text-sm text-gray-200">{{ selectedAddItem.name }} {{ selectedAddItem.level ? `+${selectedAddItem.level}` : '' }}</p>
          <div class="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-md px-2 py-1">
            <i class="fas fa-gem text-xs text-indigo-200"></i>
            <input
              v-model.number="addItemPrice"
              type="number"
              min="10"
              max="100000"
              step="1"
              class="w-full bg-transparent text-sm outline-none text-gray-200"
              placeholder="ราคา (เพชร, 10-100,000, จำนวนเต็ม)"
            />
          </div>
          <p class="text-xs text-gray-400">
            คุณจะได้รับ {{ calculateNetPrice(addItemPrice) }} เพชร (หักภาษี 35%)
          </p>
        </div>
        <!-- Actions -->
        <div class="flex gap-2">
          <button
            @click="addItemToShop"
            class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-indigo-700"
            :disabled="isSaving || addItemPrice < 10 || addItemPrice > 100000"
          >
            <span v-if="!isSaving"><i class="fas fa-check mr-2 text-xs"></i> เพิ่ม</span>
            <span v-else class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
          </button>
          <button
            @click="showAddPriceModal = false"
            class="bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-gray-600"
          >
            <i class="fas fa-times mr-2 text-xs"></i> ยกเลิก
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { useInventoryStore } from '@/composables/useInventoryStore';
import gameItems from '@/data/gameItems.json';
import defaultItemImg from '@/assets/default-item.png';
import DetailItemModal from '@/components/DetailItemModal.vue';

export default {
  props: {
    eventId: {
      type: String,
      required: true,
    },
  },
  components: { DetailItemModal },
  emits: ['close'],
  setup(props, { emit }) {
    const { user } = getUser();
    const isLoading = ref(true);
    const isClosed = ref(false);
    const isCanceling = ref(false);
    const isConfirmingCancel = ref(false);
    const isSaving = ref(false);
    const message = ref('');
    const messageIsError = ref(false);
    const shopName = ref('');
    const hideIdentity = ref(false);
    const editedShopName = ref('');
    const isEditingName = ref(false);
    const items = ref([]);
    const editedPrices = ref({});
    const expiresAt = ref(null);
    const timeLeft = ref(null);
    const showAddItemModal = ref(false);
    const showAddPriceModal = ref(false);
    const showDetailModal = ref(false);
    const selectedItem = ref(null);
    const availableItems = ref([]);
    const selectedAddItem = ref(null);
    const addItemPrice = ref(10);
    const marketplaceActions = projectFunctions.httpsCallable('gameActions');
    let timer = null;
    let confirmTimeout = null;
    let unsubscribeEvent = null;

    const restrictedTypes = [
      'หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ',
      'กางเกง', 'สนับแข้ง', 'รองเท้า', 'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ',
      'อาวุธสำรอง', 'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์'
    ];

    const hydrateItem = (instanceId, item = {}) => {
      // Try templateId first, then fallback to name for gameItems lookup
      let tpl = gameItems[item.templateId] || {};
      if (!tpl.name && item.name) {
        tpl = gameItems[item.name] || {};
      }
      const states = item.states || {};
      return {
        id: instanceId,
        instanceId,
        templateId: item.templateId || item.name,
        ...tpl,
        ...states,
        ...item, // <-- Event data (price, etc.) must override template defaults
        states,
        // Explicitly preserve event-level price (do not let template price override)
        price: item.price,
        transfer: states.transfer ?? tpl.transfer ?? false,
        rarity: states.rarity ?? tpl.rarity ?? 1,
        rarityColors: states.rarityColors ?? tpl.rarityColors ?? null,
        level: states.level ?? null,
        cardSlots: states.cardSlots || [],
        cardIds: (states.cardSlots || []).map(slot => slot.cardTemplateId || slot).filter(Boolean),
        // Include stats from gameItems.json for display
        stats: tpl.stats || {},
        upgradeStats: states.upgradeStats || {},
        cardStats: states.cardStats || {},
      };
    };

    const canListItem = (item) => {
      if (!item.transfer) return false;
      if (restrictedTypes.includes(item.type) && (item.rarity || 1) < 8) return false;
      if (item.type === 'อวัยวะจักรกล' && ((item.cardSlots?.length || 0) > 0 || (item.cardIds?.length || 0) > 0)) return false;
      return true;
    };

    const isDanger = computed(() => (timeLeft.value ?? 0) <= 300000); // 5 minutes

    // Format time for countdown
    const formatTime = (ms) => {
      if (ms <= 0) return 'หมดเวลา!';
      const seconds = Math.floor((ms / 1000) % 60);
      const minutes = Math.floor((ms / (1000 * 60)) % 60);
      const hours = Math.floor(ms / (1000 * 60 * 60));
      return `${hours}:${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
    };

    // Calculate net price after 35% tax
    const calculateNetPrice = (price) => {
      if (!price || price < 10 || price > 100000) return 0;
      return Math.floor(price * (1 - 0.35));
    };

    // Handle image error
    const handleImageError = (event) => {
      event.target.src = defaultItemImg;
    };

    // Start countdown timer
    const startCountdown = () => {
      if (expiresAt.value && !timer) {
        timeLeft.value = expiresAt.value - Date.now();
        if (timeLeft.value > 0) {
          timer = setInterval(() => {
            const now = Date.now();
            timeLeft.value = expiresAt.value - now;
            if (timeLeft.value <= 0) {
              clearInterval(timer);
              timer = null;
              message.value = 'ร้านค้าหมดเวลา!';
              messageIsError.value = false;
            }
          }, 1000);
        } else {
          timeLeft.value = 0;
          message.value = 'ร้านค้าหมดเวลา!';
          messageIsError.value = false;
        }
      }
    };

    // Stop countdown timer
    const stopCountdown = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    // Close modal
    const closeModal = () => {
      isClosed.value = true;
      stopCountdown();
      emit('close');
    };

    // Toggle hide identity
    const toggleHideIdentity = async () => {
      if (isSaving.value) return;
      const newHideIdentity = !hideIdentity.value;
      isSaving.value = true;
      try {
        await marketplaceActions({
          action: 'marketplaceAction',
          subAction: 'updateShop',
          eventId: props.eventId,
          hideIdentity: newHideIdentity,
        });
        hideIdentity.value = newHideIdentity;
        message.value = `อัปเดตการปิดบังตัวตน${newHideIdentity ? 'เปิด' : 'ปิด'}สำเร็จ!`;
        messageIsError.value = false;
      } catch (err) {
        message.value = err.message || 'เกิดข้อผิดพลาดในการอัปเดตการปิดบังตัวตน';
        messageIsError.value = true;
      } finally {
        isSaving.value = false;
      }
    };

    // Start editing shop name
    const startEditingName = () => {
      editedShopName.value = shopName.value;
      isEditingName.value = true;
    };

    // Save shop name
    const saveShopName = async () => {
      if (isSaving.value) return;
      isSaving.value = true;
      try {
        await marketplaceActions({
          action: 'marketplaceAction',
          subAction: 'updateShop',
          eventId: props.eventId,
          shopName: editedShopName.value,
        });
        shopName.value = editedShopName.value;
        message.value = 'อัปเดตชื่อร้านค้าสำเร็จ!';
        messageIsError.value = false;
        isEditingName.value = false;
      } catch (err) {
        message.value = err.message || 'เกิดข้อผิดพลาดในการอัปเดตชื่อร้านค้า';
        messageIsError.value = true;
      } finally {
        isSaving.value = false;
      }
    };

    // Start editing price for an item
    const startEditingPrice = (itemId, currentPrice) => {
      editedPrices.value[itemId] = currentPrice;
    };

    // Save item price
    const saveItemPrice = async (itemId, index) => {
      const newPrice = editedPrices.value[itemId];
      if (newPrice < 10 || newPrice > 100000) {
        message.value = 'ราคาต้องอยู่ระหว่าง 10-100,000 เพชร';
        messageIsError.value = true;
        return;
      }
      isSaving.value = true;
      try {
        await marketplaceActions({
          action: 'marketplaceAction',
          subAction: 'updateShop',
          eventId: props.eventId,
          updatedItem: { instanceId: itemId, price: newPrice },
        });
        items.value[index].price = newPrice;
        message.value = 'อัปเดตราคาสินค้าสำเร็จ!';
        messageIsError.value = false;
        delete editedPrices.value[itemId];
      } catch (err) {
        message.value = err.message || 'เกิดข้อผิดพลาดในการอัปเดตราคา';
        messageIsError.value = true;
      } finally {
        isSaving.value = false;
      }
    };

    // Remove item from shop
    const removeItemFromShop = async (itemId, index) => {
      if (isSaving.value) return;
      if (!confirm(`ยืนยันที่จะยกเลิกขายไอเทม "${items.value[index].name}"?`)) return;
      isSaving.value = true;
      try {
        await marketplaceActions({
          action: 'marketplaceAction',
          subAction: 'updateShop',
          eventId: props.eventId,
          removedItems: [itemId],
        });
        items.value.splice(index, 1);
        message.value = 'ยกเลิกขายไอเทมสำเร็จ!';
        messageIsError.value = false;
      } catch (err) {
        message.value = err.message || 'เกิดข้อผิดพลาดในการยกเลิกขายไอเทม';
        messageIsError.value = true;
      } finally {
        isSaving.value = false;
      }
    };

    // Open add item modal and load available items
    const openAddItemModal = async () => {
      await loadAvailableItems();
      showAddItemModal.value = true;
    };

    const inventoryStore = useInventoryStore();

    // Load available items from bag
    const loadAvailableItems = () => {
      if (!user.value) return;
      const bagData = inventoryStore.bagData;
      if (!bagData || !bagData.items) { availableItems.value = []; return; }
      const bagItems = bagData.items || {};
      if (Array.isArray(bagItems) || Object.keys(bagItems).length === 0) { availableItems.value = []; return; }
      const currentItemIds = new Set(items.value.map(i => i.id));
      availableItems.value = Object.entries(bagItems)
        .map(([instanceId, item]) => hydrateItem(instanceId, item))
        .filter(item => !currentItemIds.has(item.id) && canListItem(item));
    };

    // Items for add picker
    const itemsForAdd = computed(() => availableItems.value.sort((a, b) => (a.name || '').localeCompare(b.name || '')));

    // Select item to add and open price modal
    const selectAddItem = (item) => {
      selectedAddItem.value = item;
      addItemPrice.value = 10;
      showAddPriceModal.value = true;
    };

    const openItemDetail = (item) => {
      selectedItem.value = item;
      showDetailModal.value = true;
    };

    const closeDetailModal = () => {
      showDetailModal.value = false;
      selectedItem.value = null;
    };

    // Add item to shop
    const addItemToShop = async () => {
      if (addItemPrice.value < 10 || addItemPrice.value > 100000) {
        message.value = 'ราคาต้องอยู่ระหว่าง 10-100,000 เพชร';
        messageIsError.value = true;
        return;
      }
      isSaving.value = true;
      try {
        await marketplaceActions({
          action: 'marketplaceAction',
          subAction: 'updateShop',
          eventId: props.eventId,
          addedItem: { instanceId: selectedAddItem.value.instanceId || selectedAddItem.value.id, price: addItemPrice.value },
        });
        // Reload items to reflect the addition
        await fetchItems();
        message.value = 'เพิ่มสินค้าสำเร็จ!';
        messageIsError.value = false;
        showAddPriceModal.value = false;
        showAddItemModal.value = false;
      } catch (err) {
        message.value = err.message || 'เกิดข้อผิดพลาดในการเพิ่มสินค้า';
        messageIsError.value = true;
      } finally {
        isSaving.value = false;
      }
    };

    // Handle cancel shop click with confirmation
    const handleCancelShopClick = () => {
      if (isCanceling.value) return;
      if (isConfirmingCancel.value) {
        cancelShop();
        isConfirmingCancel.value = false;
        if (confirmTimeout) {
          clearTimeout(confirmTimeout);
          confirmTimeout = null;
        }
      } else {
        isConfirmingCancel.value = true;
        message.value = 'คลิกอีกครั้งเพื่อยืนยันการปิดร้านค้า';
        messageIsError.value = false;
        confirmTimeout = setTimeout(() => {
          isConfirmingCancel.value = false;
          message.value = '';
        }, 5000);
      }
    };

    // Cancel shop via Cloud Function
    const cancelShop = async () => {
      if (isCanceling.value) return;
      isCanceling.value = true;
      message.value = 'กำลังปิดร้านค้า...';
      messageIsError.value = false;
      try {
        const response = await marketplaceActions({
          action: 'marketplaceAction',
          subAction: 'closeShop',
          eventId: props.eventId,
        });
        message.value = response.data.message;
        messageIsError.value = false;
        emit('close');
      } catch (err) {
        message.value = err.message || 'เกิดข้อผิดพลาดในการปิดร้านค้า';
        messageIsError.value = true;
      } finally {
        isCanceling.value = false;
      }
    };

    // Fetch shop data including items and hideIdentity
    const fetchData = async () => {
      if (!user.value) {
        message.value = 'กรุณาล็อกอิน';
        messageIsError.value = true;
        isLoading.value = false;
        return;
      }
      try {
        unsubscribeEvent = projectFirestore.collection('events').doc(props.eventId)
          .onSnapshot((doc) => {
            if (doc.exists) {
              const data = doc.data();
              if (data.type !== 'marketplace') {
                message.value = 'อีเวนต์นี้ไม่ใช่ร้านค้า';
                messageIsError.value = true;
                isLoading.value = false;
                closeModal();
                return;
              }
              shopName.value = data.shopName || '';
              hideIdentity.value = data.hideIdentity || false;
              items.value = (data.items || []).map(item => hydrateItem(item.instanceId || item.id, item));
              expiresAt.value = data.expiresAt?.toDate().getTime() || null;
              if (expiresAt.value) startCountdown();
            } else {
              message.value = 'ไม่พบข้อมูลร้านค้า';
              messageIsError.value = true;
            }
            isLoading.value = false;
          }, (err) => {
            message.value = 'ไม่สามารถโหลดข้อมูลร้านค้าได้';
            messageIsError.value = true;
            isLoading.value = false;
          });
      } catch (err) {
        message.value = 'ไม่สามารถโหลดข้อมูลร้านค้าได้';
        messageIsError.value = true;
        isLoading.value = false;
      }
    };

    // Helper to reload items after add/remove
    const fetchItems = async () => {
      if (!unsubscribeEvent) return;
      // Trigger snapshot by refetching or rely on realtime
      const doc = await projectFirestore.collection('events').doc(props.eventId).get();
      if (doc.exists) {
        const data = doc.data();
        items.value = (data.items || []).map(item => hydrateItem(item.instanceId || item.id, item));
      }
    };

    // Lifecycle hooks
    onMounted(() => {
      fetchData();
    });

    onUnmounted(() => {
      stopCountdown();
      if (confirmTimeout) clearTimeout(confirmTimeout);
      if (unsubscribeEvent) unsubscribeEvent();
    });

    return {
      isClosed,
      isLoading,
      isCanceling,
      isConfirmingCancel,
      isSaving,
      message,
      messageIsError,
      shopName,
      hideIdentity,
      editedShopName,
      isEditingName,
      items,
      editedPrices,
      expiresAt,
      timeLeft,
      showAddItemModal,
      showAddPriceModal,
      showDetailModal,
      selectedItem,
      availableItems,
      selectedAddItem,
      addItemPrice,
      defaultItemImg,
      itemsForAdd,
      isDanger,
      formatTime,
      calculateNetPrice,
      handleImageError,
      closeModal,
      toggleHideIdentity,
      startEditingName,
      saveShopName,
      startEditingPrice,
      saveItemPrice,
      removeItemFromShop,
      openAddItemModal,
      selectAddItem,
      addItemToShop,
      handleCancelShopClick,
      openItemDetail,
      closeDetailModal,
    };
  },
};
</script>

<style scoped>
/* General Layout */
.max-h-96 { max-height: 24rem; }
.overflow-y-auto { overflow-y: auto; }

/* Animations */
.animate-spin { animation: spin 1s linear infinite; }
.animate-spin-pulse { animation: spin-pulse 1.2s ease-in-out infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes spin-pulse {
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.2); }
  100% { transform: rotate(360deg) scale(1); }
}
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.3s ease-out; }
@keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.3s ease-out; }

/* Scrollbar */
.scrollbar-thin::-webkit-scrollbar { width: 5px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #374151; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #6b7280; }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: #4b5563 #374151; }

/* Buttons */
button:disabled { opacity: 0.6; cursor: not-allowed; }
button:not(:disabled):active { transform: scale(0.98); }

/* Chips */
.chip {
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  line-height: 0.95rem;
  border: 1px solid #374151; /* gray-700 */
}
.chip-amber {
  background: rgba(120, 53, 15, 0.2); /* amber/yellow-900 @ 20% */
  color: #fde68a; /* yellow-200 */
  border-color: #92400e; /* amber/yellow-700 */
}

/* Text Sizes */
.text-2xs { font-size: 0.7rem; line-height: 0.95rem; }

/* Fade Transition */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
